import Linebreak from "../linebreak";
import getBoxImage from "./get-box-image";
import { DownArrow } from "../svg";
import MultiplicationSymbol from "@/components/multiplication-symbol";
import { validateInput } from "./form-w-validation";
import FragilePackingNotice from "./fragile-packing-notice";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { useRouter } from "next/router";
import {
  isOversized,
  isTooHeavyForSinglePersonLift,
} from "@/lib/shipment-functions";

const JS_BOXES = [
  {
    name: "A4 Envelope",
    length: "24",
    width: "34",
    height: "1",
  },
  {
    name: "Slim 1 kg",
    length: "23",
    width: "15",
    height: "9",
  },
  {
    name: "Slim 1.5 kg",
    length: "26",
    width: "26",
    height: "9",
  },
  {
    name: "Slim 3.5 kg",
    length: "44",
    width: "39",
    height: "10",
  },
  {
    name: "Standard 5 kg",
    length: "33",
    width: "24",
    height: "31",
  },
  {
    name: "Standard 7 kg",
    length: "42",
    width: "26",
    height: "32",
  },
  {
    name: "Standard 12 kg",
    length: "49",
    width: "34",
    height: "36",
  },
  {
    name: "Standard 17 kg",
    length: "56",
    width: "43",
    height: "35",
  },
  {
    name: "Standard 22.5 kg",
    length: "43",
    width: "40",
    height: "65",
  },
  {
    name: "Standard 62 kg",
    length: "61",
    width: "120",
    height: "42",
  },
];

export default function BoxWithInput({
  packingType,
  packageIndex,
  length,
  width,
  height,
  weight,
  updatePackage,
  parcel,
  userBoxes,
  ...props
}) {
  const [boxImage, setBoxImage] = useState();
  const [showFragileNotice, setShowFragileNotice] = useState(true);
  const boxes = userBoxes.concat(JS_BOXES);
  const [selectedBoxOption, setSelectedBoxOption] = useState(
    boxes.findIndex((ele) => ele.name === parcel.name)
  );
  const router = useRouter();
  const onGetQuote = router.route.includes("/get-a-quote");

  useEffect(() => {
    setBoxImage(getBoxImage(packingType, length, width, height));
  }, [width, length, height, packingType]);

  useEffect(() => {
    try {
      const res = localStorage.getItem("fragile-notice-dismissed");
      if (res === "true") {
        setShowFragileNotice(false);
      }
    } catch (err) {}
  }, []);

  return (
    <>
      {packingType === "Fragile"
        ? showFragileNotice && (
            <div className="mb-10">
              <FragilePackingNotice />
            </div>
          )
        : null}

      <div
        id="box-start"
        className="bg-bg-purple pt-4 md:pt-5 md:mt-0 scroll-mt-[80px]"
      >
        <div className="md:mb-4 text-sm">
          {packingType === "No" ? (
            <div className="grid md:grid-cols-2 px-6 md:px-8">
              <div className="order-last md:order-first place-self-center">
                {boxImage}
              </div>
              <div
                className={classNames(
                  "text-sm text-left my-4 mr-4 pb-6 md:pb-0",
                  "prose marker:text-subtext-gray",
                  onGetQuote ? "hidden md:block" : ""
                )}
              >
                <p className="font-semibold">Don’t want to top-up later?</p>
                <ul>
                  <li>
                    Measure from the longest cross-section of each side of your
                    box
                  </li>
                  <li>Round up to the nearest 0.5 cm when you measure</li>
                </ul>
                <p>
                  Don’t worry about over-declaring, we will refund you the
                  excess if that happens.
                </p>
              </div>
            </div>
          ) : packingType ? (
            <div className="flex flex-col items-center justify-center text-light-purple text-center">
              <p className="px-4 pb-2 md:w-2/3">
                {boxes?.[selectedBoxOption]?.name === "Standard 62 kg" ? (
                  <span>
                    Choose this box only for{" "}
                    <strong>
                      <i>huge</i>
                    </strong>{" "}
                    items! Smaller items are safer packed separately in smaller
                    boxes like the standard 17kg box.
                  </span>
                ) : (
                  "To order, estimate the box size needed for your unpacked items"
                )}
              </p>
              {boxImage}
              <p>
                <span>
                  {selectedBoxOption == -1
                    ? packingType === "No"
                      ? "Own Packaging"
                      : "Custom Box"
                    : boxes[selectedBoxOption].name}{" "}
                </span>
                {length && width && height
                  ? `(${length} x ${width} x ${height})`
                  : ""}
              </p>
            </div>
          ) : null}
        </div>

        <Linebreak className="mt-1 mb-0" />
        <div className="w-full px-8 md:px-16 lg:px-24 pb-2">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-x-2 py-8 sm:py-6">
            <div className="flex flex-col mb-4 sm:mb-0 relative">
              <p className="mb-2">Packaging Type</p>
              <div className="relative grid grid-cols-[auto_30px] ">
                <div className="relative bg-white rounded-xl">
                  <select
                    id={`box-selection-p${packageIndex}`}
                    name="box-selection"
                    className={classNames(
                      "rounded-xl border",
                      "border-border-gray shadow-border",
                      "focus:border-light-purple focus:shadow-border-focus focus:outline-none",
                      "w-full  h-[40px] appearance-none bg-transparent pl-3 pr-6"
                    )}
                    value={selectedBoxOption}
                    onChange={(event) => {
                      if (event.target.value !== "-1") {
                        const dimensions = { ...boxes[event.target.value] };
                        dimensions.validLength = true;
                        dimensions.validWidth = true;
                        dimensions.validHeight = true;
                        delete dimensions.weight; //so that package weight will not be overwritten
                        updatePackage("package", dimensions, packageIndex);
                      }
                      setSelectedBoxOption(event.target.value);
                    }}
                  >
                    {packingType === "No" ? (
                      <option value="-1">Own Packaging</option>
                    ) : (
                      <option value="-1">Custom Box</option>
                    )}
                    {boxes.map(({ name, length, width, height }, index) => (
                      <option key={name} value={index}>
                        {name +
                          " (" +
                          length +
                          " x " +
                          width +
                          " x " +
                          height +
                          ")"}
                      </option>
                    ))}
                  </select>
                  <DownArrow
                    color="#373F41"
                    className="absolute w-[12px] h-[8px] right-3.5 top-[45%]"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <p className="sm:mb-2">Dimensions</p>
              <div className="grid grid-cols-[auto_30px] items-center mb-4 sm:mb-0">
                <div className="flex items-center justify-between">
                  <input
                    type="text"
                    inputMode="decimal"
                    id={`length-p${packageIndex}`}
                    className={`form-input w-[65px] sm:w-[55px] h-[40px] text-center text-dark-purple font-semibold ${
                      parcel.validLength === false &&
                      parcel.validLength !== "none"
                        ? "border-error-red shadow-border-error"
                        : "border-transparent shadow-none"
                    }`}
                    value={length}
                    onBlur={(e) => {
                      if (e.target.value > 0)
                        updatePackage("validLength", true, packageIndex);
                      else updatePackage("validLength", false, packageIndex);
                    }}
                    onChange={(event) => {
                      validateInput("float", event.target.value, (value) =>
                        updatePackage("length", value, packageIndex)
                      );
                      setSelectedBoxOption(-1);
                    }}
                    {...props}
                  />
                  <MultiplicationSymbol className="mt-0" />
                  <input
                    type="text"
                    inputMode="decimal"
                    id={`width-p${packageIndex}`}
                    className={`form-input w-[65px] sm:w-[55px] h-[40px] text-center text-dark-purple font-semibold ${
                      parcel.validWidth === false &&
                      parcel.validWidth !== "none"
                        ? "border-error-red shadow-border-error"
                        : "border-transparent"
                    }`}
                    value={width}
                    onBlur={(e) => {
                      if (e.target.value > 0)
                        updatePackage("validWidth", true, packageIndex);
                      else updatePackage("validWidth", false, packageIndex);
                    }}
                    onChange={(event) => {
                      validateInput("float", event.target.value, (value) =>
                        updatePackage("width", value, packageIndex)
                      );
                      setSelectedBoxOption(-1);
                    }}
                    {...props}
                  />
                  <MultiplicationSymbol className="mt-0" />
                  <input
                    type="text"
                    inputMode="decimal"
                    id={`height-p${packageIndex}`}
                    className={`form-input w-[65px] sm:w-[55px] h-[40px] text-center text-dark-purple font-semibold ${
                      parcel.validHeight === false &&
                      parcel.validHeight !== "none"
                        ? "border-error-red shadow-border-error"
                        : "border-transparent"
                    }`}
                    value={height}
                    onBlur={(e) => {
                      if (e.target.value > 0)
                        updatePackage("validHeight", true, packageIndex);
                      else updatePackage("validHeight", false, packageIndex);
                    }}
                    onChange={(event) => {
                      validateInput("float", event.target.value, (value) =>
                        updatePackage("height", value, packageIndex)
                      );
                      setSelectedBoxOption(-1);
                    }}
                    {...props}
                  />
                </div>
                <span className="text-light-purple ml-2 sm:ml-1">cm</span>
              </div>
            </div>
            <div className="flex flex-col">
              <p className="sm:mb-2">Weight</p>
              <div className="grid grid-cols-[auto_30px] items-center">
                <input
                  type="text"
                  inputMode="decimal"
                  id={`weight-p${packageIndex}`}
                  className={`form-input w-full sm:w-[55px] h-[40px] text-dark-purple font-semibold text-center ${
                    parcel.validWeight === false &&
                    parcel.validWeight !== "none"
                      ? "border-error-red shadow-border-error"
                      : "border-transparent"
                  }`}
                  value={weight}
                  onBlur={(e) => {
                    if (e.target.value > 0)
                      updatePackage("validWeight", true, packageIndex);
                    else updatePackage("validWeight", false, packageIndex);
                  }}
                  onChange={(event) =>
                    validateInput("float", event.target.value, (value) =>
                      updatePackage("weight", value, packageIndex)
                    )
                  }
                  {...props}
                />
                <span className="text-light-purple ml-2 sm:ml-1">kg</span>
              </div>
            </div>
          </div>
          {isOversized(parcel) && <OversizedQuoteText />}
          {isTooHeavyForSinglePersonLift(parcel) && (
            <TooHeavyForSinglePersonText />
          )}
          <CustomQuoteText className="" />
        </div>
      </div>
    </>
  );
}

const OversizedQuoteText = () => {
  return (
    <p className="text-xs text-pink font-semibold pb-4" id="oversized-warning">
      Your package is oversized and will be charged minimally at 68kg.
    </p>
  );
};

const TooHeavyForSinglePersonText = () => {
  return (
    <p
      className="text-xs font-semibold text-subheading-gray pb-4"
      id="too-heavy-info"
    >
      {"Note: Your package is heavy (>30kg). For our courier's safety, "}
      {
        "please split it into smaller packages or have someone assist our courier during collection. Thank you!"
      }
    </p>
  );
};

const CustomQuoteText = ({ className }) => {
  return (
    <p className={`text-xs text-subtext-gray ${className}`}>
      For large fragile items like paintings, speakers, monitors, and bicycles,
      please{" "}
      <a
        href="https://wa.me/6591187971"
        className="text-dark-purple hover:text-light-purple"
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={-1}
      >
        WhatsApp us
      </a>{" "}
      for a custom quote.
    </p>
  );
};
